import { estadosDoAcessoTraseiro, vidroTraseiroEFixo } from './acessoTraseiro'
import type { MapaEstados } from './estadoPecas'
import {
  sufixoAncora,
  type ConfiguracaoSrs,
  type PerfilVeiculo,
} from './perfilVeiculo'

/**
 * Procedimentos internos: o que a equipe faz DENTRO da cabine antes de cortar
 * qualquer coisa — abaixar vidros, puxar o freio de mão, proteger o airbag,
 * cobrir a vítima, mexer nos bancos.
 *
 * Reaproveita o mesmo mecanismo de `teto.ts`: cada procedimento feito vira um
 * patch de `MapaEstados`, e o cálculo de obstáculos/folga da rota — que já lê
 * o estado das peças — reage sem nenhuma ligação nova. Não existe uma segunda
 * conta de "espaço ganho": o ganho aparece onde ele já aparecia, em "O que
 * está no caminho".
 */
export type IdProcedimento =
  | 'abaixarVidros'
  | 'freioDeMao'
  | 'protegerAirbag'
  | 'cobrirVitima'
  | 'colocarColarCervical'
  | 'recuarBanco'
  | 'retirarApoioCabeca'
  | 'reclinarEncosto'
  | 'removerVidroTraseiro'
  | 'rebaterBancosTraseiros'

export interface ProcedimentoInterno {
  id: IdProcedimento
  nome: string
  /** por que se faz — é este texto que o aluno lê no painel */
  porque: string
  /** posição recomendada na sequência; não é imposta, é sugerida */
  ordem: number
  /** procedimentos que precisam ter sido feitos antes deste */
  requer: readonly IdProcedimento[]
}

export const PROCEDIMENTOS: Readonly<Record<IdProcedimento, ProcedimentoInterno>> = {
  abaixarVidros: {
    id: 'abaixarVidros',
    nome: 'Abaixar os vidros',
    porque:
      'Enquanto a bateria ainda alimenta os vidros elétricos, abaixá-los evita ter de quebrá-los depois — e dá acesso a travas e fechaduras pelo vão.',
    ordem: 1,
    requer: [],
  },
  freioDeMao: {
    id: 'freioDeMao',
    nome: 'Puxar o freio de mão',
    porque: 'Impede que o veículo se desloque durante a manobra, mesmo em câmbio neutro ou pane elétrica.',
    ordem: 2,
    requer: [],
  },
  protegerAirbag: {
    id: 'protegerAirbag',
    nome: 'Proteger o airbag',
    porque:
      'Um módulo de airbag intacto ainda pode disparar. Proteger ou neutralizá-lo evita esse risco durante o corte e a retirada.',
    ordem: 3,
    requer: [],
  },
  cobrirVitima: {
    id: 'cobrirVitima',
    nome: 'Cobrir a vítima',
    porque: 'Protege contra estilhaços de vidro e faíscas do corte que vem a seguir.',
    ordem: 4,
    requer: [],
  },
  colocarColarCervical: {
    id: 'colocarColarCervical',
    nome: 'Colocar o colar cervical',
    porque:
      'Imobiliza a coluna cervical em posição neutra antes de manipular o banco, retirar o encosto de cabeça ou movimentar a vítima.',
    ordem: 5,
    requer: [],
  },
  recuarBanco: {
    id: 'recuarBanco',
    nome: 'Recuar o banco no trilho',
    porque: 'Abre espaço entre o corpo e o volante/painel antes de qualquer outra manobra no banco.',
    ordem: 6,
    requer: [],
  },
  retirarApoioCabeca: {
    id: 'retirarApoioCabeca',
    nome: 'Retirar o apoio de cabeça',
    porque: 'Remove o obstáculo superior do banco para permitir a reclinação total e a entrada desimpedida da prancha.',
    ordem: 7,
    requer: [],
  },
  reclinarEncosto: {
    id: 'reclinarEncosto',
    nome: 'Reclinar o encosto',
    porque: 'Reduz a altura que o corpo precisa vencer na retirada e abre espaço livre para a passagem da prancha.',
    ordem: 8,
    requer: [],
  },
  removerVidroTraseiro: {
    id: 'removerVidroTraseiro',
    nome: 'Retirar o vidro traseiro e soltar a prateleira',
    porque:
      'No sedan o vidro traseiro é fixo e a prateleira fecha a cabine por trás: abrir a tampa do porta-malas não abre passagem. Retire o vidro com a vítima protegida dos estilhaços; no hatch ele sobe junto com a tampa e este passo não existe.',
    ordem: 9,
    requer: [],
  },
  rebaterBancosTraseiros: {
    id: 'rebaterBancosTraseiros',
    nome: 'Rebater os encostos do banco traseiro',
    porque:
      'O banco de trás não reclina: o encosto deita para a frente sobre o assento. Pela traseira o corpo atravessa esse banco; em pé, o encosto fecha o vão. No hatch, rebatido, a área de carga fica contínua; no sedan a divisória do porta-malas continua atrás dele.',
    ordem: 10,
    requer: [],
  },
}

export const IDS_PROCEDIMENTO: readonly IdProcedimento[] = (
  Object.values(PROCEDIMENTOS) as ProcedimentoInterno[]
)
  .sort((a, b) => a.ordem - b.ordem)
  .map((p) => p.id)

const IDS_PORTA = [
  'portaDianteiraEsquerda',
  'portaDianteiraDireita',
  'portaTraseiraEsquerda',
  'portaTraseiraDireita',
] as const

/**
 * Estado de peça correspondente aos procedimentos já feitos.
 *
 * `abaixarVidros` e `freioDeMao` são ações no veículo inteiro. Os três que
 * mexem no banco (`recuarBanco`, `reclinarEncosto`, `retirarApoioCabeca`) são
 * escopados à âncora em foco — é ali que a equipe está trabalhando.
 */
export function estadosDosProcedimentos(
  feitos: readonly IdProcedimento[],
  idAncoraFoco: string,
  perfil?: PerfilVeiculo,
): MapaEstados {
  const mapa: Record<string, 'aberta' | 'afastada' | 'removida'> = {}
  const sufixo = sufixoAncora(idAncoraFoco)

  if (feitos.includes('abaixarVidros')) {
    for (const idPorta of IDS_PORTA) mapa[`${idPorta}.vidro`] = 'removida'
  }
  if (feitos.includes('freioDeMao')) mapa.freioDeMao = 'aberta'
  if (feitos.includes('recuarBanco')) mapa[`banco${sufixo}.assento`] = 'afastada'
  if (feitos.includes('reclinarEncosto')) mapa[`banco${sufixo}.encosto`] = 'aberta'
  if (feitos.includes('retirarApoioCabeca')) mapa[`banco${sufixo}.apoioCabeca`] = 'removida'
  // os dois da traseira mexem em peças que dependem da carroceria; sem o perfil
  // não há como saber quais bancos e que vidro existem
  if (perfil) {
    Object.assign(
      mapa,
      estadosDoAcessoTraseiro(perfil, {
        vidroEPrateleira: feitos.includes('removerVidroTraseiro'),
        bancosTraseiros: feitos.includes('rebaterBancosTraseiros'),
      }),
    )
  }

  return mapa
}

/** Dispositivos SRS que esta posição declara, elegíveis a serem protegidos. */
export function airbagsDaPosicao(
  perfil: PerfilVeiculo,
  idAncoraFoco: string,
): Array<keyof ConfiguracaoSrs> {
  const candidatos: Array<keyof ConfiguracaoSrs> =
    idAncoraFoco === 'motorista'
      ? ['airbagMotorista', 'airbagColunaMotorista', 'airbagBancoMotorista']
      : idAncoraFoco === 'passageiro'
        ? ['airbagPassageiro']
        : []
  return candidatos.filter((chave) => perfil.srs[chave])
}

/**
 * Módulos SRS neutralizados pelo procedimento — não disparam mais na
 * cinemática, mesmo que o tipo de colisão normalmente os acionasse.
 */
export function srsProtegidos(
  perfil: PerfilVeiculo,
  idAncoraFoco: string,
  feitos: readonly IdProcedimento[],
): Array<keyof ConfiguracaoSrs> {
  return feitos.includes('protegerAirbag') ? airbagsDaPosicao(perfil, idAncoraFoco) : []
}

/** A vítima está coberta pela manta — não passa por `estadoPecas`, é lido direto pela cena. */
export const vitimaEstaCoberta = (feitos: readonly IdProcedimento[]): boolean =>
  feitos.includes('cobrirVitima')

/**
 * Lista de feitos como estava um instante antes da última alternância.
 *
 * Não é um histórico guardado: é a lista atual com o próprio item somado ou
 * subtraído de volta, dependendo de qual direção o clique tomou. Serve para
 * comparar o efeito de UMA ação (Fase 4), sem se confundir com o efeito
 * acumulado de todos os procedimentos feitos até agora.
 */
export function feitosAntesDaUltimaAlternancia(
  feitosAtuais: readonly IdProcedimento[],
  ultimoAlterado: IdProcedimento,
): readonly IdProcedimento[] {
  return feitosAtuais.includes(ultimoAlterado)
    ? feitosAtuais.filter((p) => p !== ultimoAlterado)
    : [...feitosAtuais, ultimoAlterado]
}

/** Os três procedimentos cujo efeito é geométrico — mexem numa peça do banco. */
export const PROCEDIMENTOS_DO_BANCO: readonly IdProcedimento[] = [
  'recuarBanco',
  'reclinarEncosto',
  'retirarApoioCabeca',
]

/**
 * Procedimentos ainda pendentes E relevantes nesta cena — filtra
 * `protegerAirbag` quando a posição em foco não tem nenhum airbag para
 * proteger, e qualquer procedimento cujo pré-requisito ainda não foi feito.
 */
export function procedimentosPendentes(
  perfil: PerfilVeiculo,
  idAncoraFoco: string,
  feitos: readonly IdProcedimento[],
  idAbertura?: string | null,
): ProcedimentoInterno[] {
  const visiveis = new Set(procedimentosVisiveis(perfil, idAbertura))
  return IDS_PROCEDIMENTO.map((id) => PROCEDIMENTOS[id])
    .filter((p) => visiveis.has(p.id))
    .filter((p) => !feitos.includes(p.id))
    .filter((p) => p.requer.every((r) => feitos.includes(r)))
    .filter((p) => p.id !== 'protegerAirbag' || airbagsDaPosicao(perfil, idAncoraFoco).length > 0)
}

/** Os dois procedimentos da traseira: só fazem sentido quando a retirada é por lá. */
const PROCEDIMENTOS_DA_TRASEIRA: readonly IdProcedimento[] = ['removerVidroTraseiro', 'rebaterBancosTraseiros']

/**
 * Procedimentos que aparecem no painel nesta cena.
 *
 * Os da traseira só entram quando a saída é pela tampa — senão o aluno veria
 * "retirar o vidro traseiro" numa retirada pela porta. Retirar o vidro só
 * existe no sedan: no hatch ele sobe com a tampa.
 */
export function procedimentosVisiveis(perfil: PerfilVeiculo, idAbertura?: string | null): IdProcedimento[] {
  return IDS_PROCEDIMENTO.filter((id) => {
    if (!PROCEDIMENTOS_DA_TRASEIRA.includes(id)) return true
    if (idAbertura !== 'tampaTraseira') return false
    return id !== 'removerVidroTraseiro' || vidroTraseiroEFixo(perfil)
  })
}
